import { useCallback, useState } from 'react';
import { useLazyGetUserSignatureQuery } from '@/features/user/userApi';
import { NONE_SIGNATURE_OPTION } from '@/components/Drawers/AddUnlayerEmailDrawer/constants';

/**
 * Turns a normalized signature option into the insertion request consumed by the
 * Unlayer wrapper. It hides the difference between frozen Unlayer HTML and the
 * legacy URL-backed signature fetch flow.
 */
export const useSignatureInsertionRequest = ({ userDetails }) => {
	const [signatureInsertRequest, setSignatureInsertRequest] = useState(null);
	const [signatureError, setSignatureError] = useState('');
	const [getUserSignature, { isFetching: isFetchingLegacySignature }] =
		useLazyGetUserSignatureQuery();

	const queueSignatureInsertion = useCallback(
		async (signatureOption, { waitForDesignLoad = false } = {}) => {
			setSignatureError('');
			if (!signatureOption || signatureOption.id === NONE_SIGNATURE_OPTION.id) {
				setSignatureInsertRequest({
					id: `signature-clear-${Date.now()}`,
					html: '',
					waitForDesignLoad,
				});
				return;
			}

			if (signatureOption.source === 'unlayer') {
				setSignatureInsertRequest({
					id: `${signatureOption.id}-${Date.now()}`,
					html: signatureOption.html ?? '',
					waitForDesignLoad,
				});
				return;
			}

			try {
				const signatureHtml = await getUserSignature({
					url: signatureOption.signature,
					id: userDetails?.userId,
				}).unwrap();
				setSignatureInsertRequest({
					id: `${signatureOption.id}-${Date.now()}`,
					html: signatureHtml ?? '',
					waitForDesignLoad,
				});
			} catch (error) {
				console.error('Error loading legacy user signature', error);
				setSignatureError('Unable to load the selected signature.');
			}
		},
		[getUserSignature, userDetails?.userId]
	);

	return {
		isFetchingLegacySignature,
		queueSignatureInsertion,
		signatureError,
		signatureInsertRequest,
	};
};
